import CUIBox from 'connect-dot-com-ui-components/Components/CUIBox/CUIBox';
import CUIInputGroup from 'connect-dot-com-ui-components/Components/Forms/CUIInputGroup';
import CUIInputRightAddon from 'connect-dot-com-ui-components/Components/Forms/CUIInputRightAddon';
import CUIInput from 'connect-dot-com-ui-components/Components/Forms/CUIInput';
import I18n from 'I18n';
import { HomepageCardBackgroundGradient } from 'connect-dot-com-ui-components/Components/CUIHomepageCard/utils/backgroundGradients';
import { CUIFlex } from 'connect-dot-com-ui-components/Components/CUIFlex/CUIFlex';
import CUIButton from 'connect-dot-com-ui-components/Components/CUIButton/CUIButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { getIsUsernameAvailable } from '../../api/profilesApi';
import debounce from 'react-utils/debounce';
import { CUIFormControl, CUIFormErrorMessage } from 'connect-dot-com-ui-components/Components/Forms/CUIFormControl';
import CUIText from 'connect-dot-com-ui-components/Components/CUIText/CUIText';
import { checkUsernameAvailabilityReducer, checkUsernameAvailabilityReducerInitialState } from '../../utils/reducers/checkUsernameAvailabilityReducer';
import { Storage } from 'connect-dot-com-lib/storage/superstore';
import { getSignupUrl } from '../../utils/urls';
import { redirectTo } from 'connect-dot-com-lib/utils/windowUtils';
import { useFormFieldState } from 'connect-dot-com-lib/hooks/formHooks';
import { generateValidationFunction } from 'connect-dot-com-lib/utils/forms/validation';
import { generateBasicBreakpointObject } from 'connect-dot-com-ui-components/utils/layout';
import { CUIVStack } from 'connect-dot-com-ui-components/Components/CUIStack/CUIStack';
import { TrackerContext } from 'connect-dot-com-ui-components/context/TrackerContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MIN_LENGTH = 4;
function validateUsernameField(value) {
  const validationFunction = generateValidationFunction({
    minLength: {
      value: MIN_LENGTH,
      error: I18n.text('app.homepage.claimUsername.formErrors.tooShort')
    },
    validCharacters: {
      value: /^[a-zA-Z0-9_-]*$/,
      error: I18n.text('app.homepage.claimUsername.formErrors.alphanumericOnly')
    }
  });
  return validationFunction(value);
}
const CheckUsernameAvailable = () => {
  const {
    trackInteraction
  } = useContext(TrackerContext);
  function trackCreateProfileClick() {
    trackInteraction('click create profile');
  }
  const formFieldState = useFormFieldState(validateUsernameField, '');
  const [{
    hasFetched,
    isAvailable,
    isFetching
  }, dispatch] = useReducer(checkUsernameAvailabilityReducer, checkUsernameAvailabilityReducerInitialState);
  const hasError = formFieldState.errors.length > 0 && formFieldState.touched;
  const isValid = !hasError && isAvailable;
  const handleTestUsernameAvailability = useCallback(username => {
    dispatch({
      type: 'fetch'
    });
    getIsUsernameAvailable(username).then(async isAvailableResp => {
      dispatch({
        type: 'receive',
        isAvailable: isAvailableResp,
        validatedUsername: username
      });
      if (isAvailableResp) {
        // Need to pick this up in onboarding
        // https://git.hubteam.com/HubSpot/connect-dot-com-app-ui/issues/1855
        await new Storage().set('desired_username', username);
      }
    }).catch(() => {});
  }, []);
  const debouncedHandleTestUsernameAvailability = useMemo(() => debounce(handleTestUsernameAvailability, 500), [handleTestUsernameAvailability]);
  useEffect(() => {
    if (!hasError && formFieldState.value) {
      debouncedHandleTestUsernameAvailability(formFieldState.value);
    }
  }, [hasError, formFieldState.value, debouncedHandleTestUsernameAvailability]);
  const rightInputBorderAttributes = isValid ? {
    border: 'none'
  } : {
    borderColor: 'CARNABY.medium'
  };
  const handleChange = event => {
    formFieldState.onChange(event.target.value);
    dispatch({
      type: 'reset'
    });
  };
  const errorMessage = hasError ? formFieldState.errors[0] : hasFetched && !isAvailable ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "app.homepage.claimUsername.formErrors.notUnique"
  }) : null;
  const signup = () => {
    trackCreateProfileClick();
    redirectTo(getSignupUrl());
  };
  return /*#__PURE__*/_jsx(CUIFlex, {
    direction: {
      base: 'column',
      sm: 'row'
    },
    children: /*#__PURE__*/_jsx(CUIFormControl, {
      isInvalid: !isValid,
      children: /*#__PURE__*/_jsxs(CUIVStack, {
        gap: 0,
        children: [/*#__PURE__*/_jsx(CUIBox, {
          borderRadius: "full",
          _focusWithin: {
            boxShadow: '0 0 2px 1px var(--chakra-colors-CARNABY-medium)'
          },
          children: /*#__PURE__*/_jsxs(CUIInputGroup, {
            size: "xl",
            width: generateBasicBreakpointObject('100%', '460px'),
            children: [/*#__PURE__*/_jsx(CUIInput, {
              variant: "usernameCheck",
              isInvalid: !isValid,
              minLength: 4,
              maxLength: 14,
              placeholder: I18n.text('app.homepage.claimUsername.createProfileInputPlaceholder'),
              value: formFieldState.value,
              onChange: handleChange
            }), /*#__PURE__*/_jsx(CUIInputRightAddon, Object.assign({
              padding: 0,
              borderRightRadius: "full"
            }, rightInputBorderAttributes, {
              children: /*#__PURE__*/_jsx(CUIButton, {
                isLoading: isFetching,
                width: "100%",
                padding: 0,
                isDisabled: !isValid,
                _disabled: {
                  pointerEvents: 'none',
                  background: 'GREYSCALE.SATELLITE',
                  color: 'GREYSCALE.COMET'
                },
                _hover: {
                  background: `linear-gradient(${HomepageCardBackgroundGradient.orangeToMagenta})`
                },
                background: `linear-gradient(${HomepageCardBackgroundGradient.orangeToMagenta})`,
                height: "100%",
                borderRightRadius: "full",
                borderLeftRadius: "initial",
                borderColor: "GREYSCALE.SATELLITE",
                onClick: signup,
                fontSize: "lg",
                color: "#FFF",
                children: /*#__PURE__*/_jsx(CUIText, {
                  marginLeft: 6,
                  marginRight: 6,
                  fontSize: "md",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.homepage.claimUsername.createProfile"
                  })
                })
              })
            }))]
          })
        }), errorMessage ? /*#__PURE__*/_jsx(CUIFormErrorMessage, {
          flexDir: "column",
          alignItems: "flex-start",
          pl: 5,
          children: errorMessage
        }) : /*#__PURE__*/_jsx(CUIText, {
          flexDir: "column",
          alignItems: "flex-start",
          pl: 5,
          mt: "6.5px",
          mb: "-2px",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.homepage.claimUsername.tooLate"
          })
        })]
      })
    })
  });
};
export default CheckUsernameAvailable;